<template>
  <div class="builder-view">
    <header class="builder-view__header">
      <div class="builder-view__header-left">
        <router-link :to="{ name: 'Pages' }" class="builder-view__back">
          <span class="material-symbols-outlined">arrow_back</span>
          Back
        </router-link>
        <h1 class="builder-view__title">Homepage Builder</h1>
      </div>
      <div class="builder-view__header-actions">
        <div class="builder-view__lang-toggles builder-view__lang-toggles--header">
          <button
            v-for="(lang, key) in langs"
            :key="key"
            type="button"
            class="builder-view__lang-btn"
            :class="{ 'builder-view__lang-btn--active': selectedLang === key }"
            @click="selectedLang = key"
          >
            {{ lang }}
          </button>
        </div>
        <button
          type="button"
          class="builder-view__btn builder-view__btn--preview"
          :disabled="!previewUrl || !isEdit"
          @click="openPreview"
        >
          <span class="material-symbols-outlined">visibility</span>
          Preview
        </button>
        <button
          type="button"
          class="builder-view__btn builder-view__btn--primary"
          :disabled="$v.$invalid || saving || uploadingCount > 0"
          @click="save"
        >
          <span class="material-symbols-outlined">save</span>
          Save
        </button>
      </div>
    </header>

    <div class="builder-view__body">
      <div class="builder-view__canvas">
        <div class="builder-view__page-settings">
          <div class="builder-view__page-row">
            <div class="builder-view__page-field builder-view__page-field--equal">
              <label class="builder-view__page-label">Title</label>
              <input
                v-model="form.data[selectedLang].title"
                type="text"
                class="builder-view__page-input"
                placeholder="Luxury Marble Collections"
              >
            </div>
            <div class="builder-view__page-field builder-view__page-field--equal">
              <label class="builder-view__page-label">URL SLUG</label>
              <div class="builder-view__url-wrap">
                <span class="builder-view__url-prefix">{{ urlPrefix }}/</span>
                <input
                  v-model="url[selectedLang]"
                  type="text"
                  class="builder-view__page-input builder-view__page-input--slug"
                  placeholder="home"
                  :disabled="isEdit && edit && !edit.deletable"
                  @input="$v.url[selectedLang].$touch()"
                >
              </div>
              <span v-if="$v.url[selectedLang].$invalid && $v.url[selectedLang].$dirty" class="builder-view__error">
                URL može da sadrži samo mala slova, brojeve i crtice
              </span>
            </div>
          </div>
          <div class="builder-view__page-field builder-view__page-field--full">
            <label class="builder-view__page-label">Description</label>
            <textarea
              v-model="form.data[selectedLang].meta"
              class="builder-view__page-textarea"
              rows="3"
              placeholder="Page description..."
            />
          </div>
          <div class="builder-view__page-field builder-view__page-field--full">
            <label class="builder-view__page-label">KEYWORDS</label>
            <div class="builder-view__keywords">
              <span
                v-for="(kw, i) in pageKeywords"
                :key="i"
                class="builder-view__keyword-tag"
              >
                {{ kw }}
                <button
                  type="button"
                  class="builder-view__keyword-remove"
                  aria-label="Remove keyword"
                  @click="removePageKeyword(i)"
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
              </span>
            </div>
            <div class="builder-view__keyword-input-wrap">
              <input
                v-model="newPageKeyword"
                type="text"
                class="builder-view__page-input builder-view__keyword-input"
                placeholder="Add keyword"
                @keyup.enter.prevent="addPageKeyword"
              >
            </div>
          </div>
          <div class="builder-view__page-field builder-view__page-field--full">
            <label class="builder-view__page-label">HERO IMAGE</label>
            <p class="builder-view__section-hint builder-view__hero-hint">Image displayed at the top of the page.</p>
            <div v-if="pageHeroImageDisplay" class="builder-view__hero-upload-preview">
              <img :src="pageHeroImageDisplay" alt="" class="builder-view__hero-upload-img">
              <button type="button" class="builder-view__gallery-remove builder-view__image-remove" @click="clearHeroImage">
                <span class="material-symbols-outlined">close</span>
              </button>
            </div>
            <input
              ref="heroImageInput"
              type="file"
              accept="image/*"
              class="builder-view__file-hidden"
              @change="onHeroImageFileChange"
            >
            <button
              type="button"
              class="builder-view__btn builder-view__btn--outline builder-view__hero-upload-btn"
              @click="$refs.heroImageInput && $refs.heroImageInput.click()"
            >
              <span class="material-symbols-outlined">add_photo_alternate</span>
              {{ pageHeroImageDisplay ? 'Replace hero image' : 'Upload hero image' }}
            </button>
          </div>
          <div class="builder-view__page-field builder-view__page-field--full">
            <label class="builder-view__page-label">FOOTER</label>
            <label class="builder-view__form-checkbox">
              <input v-model="form.footerProducts" type="checkbox">
              Show in footer "Products" section
            </label>
            <p class="builder-view__section-hint">Marked pages are listed under Products in the website footer.</p>
          </div>
        </div>

        <div class="builder-view__blocks">
          <div
            v-for="(block, index) in blocks"
            :key="block.id"
            class="builder-view__block"
            :class="{ 'builder-view__block--active': activeBlockId === block.id, 'builder-view__block--dragging': draggedBlockIndex === index }"
            draggable="true"
            @click="selectBlock(block)"
            @dragstart="onBlockDragStart($event, index)"
            @dragend="onBlockDragEnd"
            @dragover.prevent="onBlockDragOver($event, index)"
            @drop="onBlockDrop(index)"
          >
            <div class="builder-view__block-sidebar" @click.stop>
              <span class="material-symbols-outlined builder-view__block-handle" title="Drag">drag_indicator</span>
              <button
                type="button"
                class="builder-view__block-delete"
                title="Delete"
                @click.stop="removeBlock(index)"
              >
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>
            <div v-if="activeBlockId === block.id" class="builder-view__block-badge">ACTIVE BLOCK</div>

            <div
              v-if="block.type === 'text'"
              class="builder-view__block-content"
              :style="textBlockPreviewStyle(block)"
            >
              <h3 class="builder-view__block-headline">{{ block.headline || 'Headline' }}</h3>
              <div class="builder-view__block-desc" v-html="block.description || 'Description'"></div>
            </div>

            <div v-else-if="block.type === 'image'" class="builder-view__block-content builder-view__block-image">
              <div v-if="block.image || (block.preview)" class="builder-view__image-preview">
                <img :src="block.preview || block.image" alt="">
              </div>
              <div v-else class="builder-view__image-placeholder">
                <span class="material-symbols-outlined">image</span>
                <span>Image Section</span>
              </div>
              <p v-if="block.caption" class="builder-view__block-caption">{{ block.caption }}</p>
            </div>

            <div v-else-if="block.type === 'gallery'" class="builder-view__block-content builder-view__block-gallery">
              <div
                v-for="(img, i) in blockImages(block)"
                :key="i"
                class="builder-view__gallery-thumb"
              >
                <img :src="img.preview" alt="">
              </div>
              <div
                v-for="n in placeholderCount(block)"
                :key="'ph-' + n"
                class="builder-view__gallery-thumb builder-view__gallery-thumb--empty"
              >
                <span class="material-symbols-outlined">image</span>
              </div>
            </div>

            <div v-else-if="block.type === 'video'" class="builder-view__block-content builder-view__block-video">
              <div v-if="videoEmbedUrl(block.url)" class="builder-view__video-embed-wrap">
                <iframe
                  :src="videoEmbedUrl(block.url)"
                  class="builder-view__video-iframe"
                  frameborder="0"
                  allowfullscreen
                />
              </div>
              <div v-else class="builder-view__video-placeholder">
                <span class="material-symbols-outlined">videocam</span>
                <span>{{ block.url ? 'Video URL configured' : 'Video Embed' }}</span>
              </div>
              <p v-if="block.caption" class="builder-view__block-caption">{{ block.caption }}</p>
            </div>

            <div v-else-if="block.type === 'videoGallery'" class="builder-view__block-content builder-view__block-video-gallery">
              <div
                v-for="(v, i) in (block.videos || [])"
                :key="i"
                class="builder-view__video-gallery-item"
              >
                <span class="material-symbols-outlined">play_circle</span>
                <span>{{ v.url || 'Video ' + (i + 1) }}</span>
              </div>
              <div
                v-for="n in videoGalleryPlaceholderCount(block)"
                :key="'ph-' + n"
                class="builder-view__video-gallery-item builder-view__video-gallery-item--empty"
              >
                <span class="material-symbols-outlined">add</span>
                <span>Add video</span>
              </div>
            </div>

            <div v-else-if="block.type === 'tiles'" class="builder-view__block-content builder-view__block-tiles">
              <div class="builder-view__tiles-grid">
                <div
                  v-for="(tile, i) in (block.tiles || [])"
                  :key="i"
                  class="builder-view__tile-preview"
                >
                  <div class="builder-view__tile-preview-img">
                    <img v-if="tile.preview || tile.image" :src="tile.preview || tile.image" alt="">
                    <span v-else class="material-symbols-outlined">image</span>
                  </div>
                  <span class="builder-view__tile-preview-title">{{ tile.title || 'Title' }}</span>
                  <span class="builder-view__tile-preview-link">{{ tile.link ? 'Saznaj više' : 'Link' }}</span>
                </div>
                <div
                  v-for="n in tilesPlaceholderCount(block)"
                  :key="'ph-' + n"
                  class="builder-view__tile-preview builder-view__tile-preview--empty"
                >
                  <template v-if="n === 1 && !(block.tiles || []).length">
                    <span class="material-symbols-outlined">add</span>
                    <span>Add tile</span>
                  </template>
                </div>
              </div>
            </div>

            <div v-else-if="block.type === 'greySection'" class="builder-view__block-content builder-view__block-grey">
              <div class="builder-view__grey-preview">
                <div class="builder-view__grey-left">
                  <h3 class="builder-view__block-headline">{{ block.title || 'Title' }}</h3>
                  <p class="builder-view__block-desc builder-view__grey-subtitle">{{ block.subtitle || 'Subtitle' }}</p>
                </div>
                <div class="builder-view__grey-right">
                  <div class="builder-view__block-desc" v-html="block.text || 'Text'"></div>
                </div>
              </div>
            </div>

            <div v-else-if="block.type === 'ideaSection'" class="builder-view__block-content builder-view__block-idea">
              <div class="builder-view__idea-preview">
                <div class="builder-view__idea-text">
                  <div class="builder-view__block-desc" v-html="block.description || 'Description'"></div>
                </div>
                <div class="builder-view__idea-list">
                  <h3 class="builder-view__block-headline">{{ block.title || 'Title' }}</h3>
                  <ul class="builder-view__idea-fixed-list">
                    <li
                      v-for="(gallery, idx) in ideaSectionTitles(block)"
                      :key="gallery.id != null ? gallery.id : idx"
                    >
                      <span class="index">{{ idx + 1 }}</span>
                      <span>{{ gallery.title }}</span>
                    </li>
                  </ul>
                  <p v-if="!ideaSectionTitles(block).length" class="builder-view__idea-empty">Gallery folders will appear here. Add folders in Gallery.</p>
                </div>
              </div>
            </div>

            <div v-else-if="block.type === 'cooperateSection'" class="builder-view__block-content builder-view__block-cooperate">
              <div class="builder-view__cooperate-preview">
                <h3 class="builder-view__block-headline">{{ block.title || 'Title' }}</h3>
                <p class="builder-view__block-desc">{{ block.description || 'Description' }}</p>
                <span class="builder-view__cooperate-btn">Contact button</span>
              </div>
            </div>

            <div v-else-if="block.type === 'bestMaterialSection'" class="builder-view__block-content builder-view__block-best-material">
              <div class="builder-view__best-material-preview">
                <div class="builder-view__best-material-left">
                  <h3 class="builder-view__block-headline">{{ block.title || 'Title' }}</h3>
                </div>
                <div class="builder-view__best-material-right">
                  <div class="builder-view__block-desc" v-html="block.description || 'Description'"></div>
                </div>
              </div>
            </div>

            <div v-else-if="block.type === 'faq'" class="builder-view__block-content builder-view__block-faq">
              <h3 class="builder-view__block-headline">{{ block.title || 'FAQ' }}</h3>
              <ul class="builder-view__faq-preview-list">
                <li
                  v-for="(q, i) in (block.questions || [])"
                  :key="i"
                  class="builder-view__faq-preview-item"
                >
                  <span class="builder-view__faq-preview-num">{{ i + 1 }}</span>
                  <span>{{ q.title || 'Question' }}</span>
                </li>
              </ul>
              <p v-if="!(block.questions || []).length" class="builder-view__faq-preview-empty">No questions yet. Add question in settings.</p>
            </div>

            <div v-else-if="block.type === 'partnersSection'" class="builder-view__block-content builder-view__block-partners">
              <div
                v-for="(img, i) in partnersBlockImages(block)"
                :key="i"
                class="builder-view__partners-thumb"
              >
                <img :src="img.preview" alt="">
              </div>
              <div
                v-for="n in partnersPlaceholderCount(block)"
                :key="'ph-' + n"
                class="builder-view__partners-thumb builder-view__partners-thumb--empty"
              >
                <span class="material-symbols-outlined">image</span>
              </div>
            </div>

            <div v-else-if="block.type === 'materialTiles'" class="builder-view__block-content builder-view__block-material-tiles">
              <div class="builder-view__material-tiles-preview">
                <div
                  v-for="m in materialTilesPreviewMaterials(block)"
                  :key="m.id"
                  class="builder-view__material-tile-preview"
                >
                  <div
                    class="builder-view__material-tile-preview-img"
                    :style="materialTileImageStyle(m)"
                  />
                  <span class="builder-view__material-tile-preview-name">{{ m.displayName || m.name }}</span>
                </div>
                <div v-if="!materialTilesPreviewMaterials(block).length" class="builder-view__material-tiles-empty">
                  <span class="material-symbols-outlined">grid_on</span>
                  <span>Select materials in settings</span>
                </div>
              </div>
            </div>
          </div>

          <div class="builder-view__append" @click="appendBlockMode = true">
            <span class="material-symbols-outlined builder-view__append-icon">add</span>
            <span>APPEND CONTENT BLOCK</span>
          </div>
        </div>
      </div>

      <aside v-if="activeBlock || appendBlockMode" class="builder-view__sidebar">
        <div class="builder-view__sidebar-header">
          <div>
            <h3 class="builder-view__sidebar-title">{{ appendBlockMode ? 'ADD CONTENT BLOCK' : 'SETTINGS' }}</h3>
            <p v-if="activeBlock" class="builder-view__sidebar-subtitle">{{ blockTypeLabel(activeBlock.type) }}</p>
          </div>
          <button type="button" class="builder-view__sidebar-close" @click="activeBlockId = null; appendBlockMode = false">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <div v-if="appendBlockMode" class="builder-view__sidebar-body">
          <div class="builder-view__block-type-list">
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('text')">
              <span class="material-symbols-outlined">text_fields</span>
              Text Section
            </button>
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('image')">
              <span class="material-symbols-outlined">image</span>
              Image Section
            </button>
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('gallery')">
              <span class="material-symbols-outlined">photo_library</span>
              Gallery
            </button>
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('video')">
              <span class="material-symbols-outlined">videocam</span>
              Video Embed
            </button>
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('tiles')">
              <span class="material-symbols-outlined">grid_view</span>
              Tiles Section
            </button>
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('greySection')">
              <span class="material-symbols-outlined">view_agenda</span>
              Grey section
            </button>
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('ideaSection')">
              <span class="material-symbols-outlined">lightbulb</span>
              Idea section
            </button>
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('cooperateSection')">
              <span class="material-symbols-outlined">handshake</span>
              Cooperate section
            </button>
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('bestMaterialSection')">
              <span class="material-symbols-outlined">countertops</span>
              Best Material Section
            </button>
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('faq')">
              <span class="material-symbols-outlined">help</span>
              FAQ
            </button>
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('partnersSection')">
              <span class="material-symbols-outlined">groups</span>
              Partners section
            </button>
            <button type="button" class="builder-view__block-menu-item" @click="addBlock('materialTiles')">
              <span class="material-symbols-outlined">grid_on</span>
              Material tiles
            </button>
          </div>
        </div>

        <div v-else-if="activeBlock.type === 'text'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">TYPOGRAPHY</h4>
            <div class="builder-view__field">
              <label class="builder-view__label">Headline</label>
              <textarea
                v-model="activeBlock.headline"
                class="builder-view__textarea"
                rows="2"
                placeholder="Timeless Marble for Modern Spaces"
              />
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Description</label>
              <Editor
                :value="activeBlock.description"
                :init="tinymceInit"
                class="builder-view__wysiwyg"
                @input="activeBlock.description = $event"
              />
            </div>
          </section>

          <section class="builder-view__section">
            <h4 class="builder-view__section-title">APPEARANCE</h4>
            <div class="builder-view__field">
              <label class="builder-view__label">Padding Y</label>
              <input
                v-model.number="activeBlock.paddingY"
                type="number"
                class="builder-view__input"
                min="0"
              >
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Max Width</label>
              <input
                v-model.trim="activeBlock.maxWidth"
                type="text"
                class="builder-view__input"
                placeholder="e.g. 800px, 1200px, 100%"
              >
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Text Align</label>
              <div class="builder-view__align-btns">
                <button
                  type="button"
                  class="builder-view__align-btn"
                  :class="{ 'builder-view__align-btn--active': activeBlock.textAlign === 'left' }"
                  @click="setTextAlign('left')"
                >
                  <span class="material-symbols-outlined">format_align_left</span>
                </button>
                <button
                  type="button"
                  class="builder-view__align-btn"
                  :class="{ 'builder-view__align-btn--active': activeBlock.textAlign === 'center' }"
                  @click="setTextAlign('center')"
                >
                  <span class="material-symbols-outlined">format_align_center</span>
                </button>
                <button
                  type="button"
                  class="builder-view__align-btn"
                  :class="{ 'builder-view__align-btn--active': activeBlock.textAlign === 'right' }"
                  @click="setTextAlign('right')"
                >
                  <span class="material-symbols-outlined">format_align_right</span>
                </button>
              </div>
            </div>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'greySection'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">GREY SECTION</h4>
            <div class="builder-view__field">
              <label class="builder-view__label">Title</label>
              <textarea
                v-model="activeBlock.title"
                class="builder-view__textarea"
                rows="2"
                placeholder="Section title"
              />
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Subtitle</label>
              <textarea
                v-model="activeBlock.subtitle"
                class="builder-view__textarea"
                rows="2"
                placeholder="Section subtitle"
              />
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Text</label>
              <Editor
                :value="activeBlock.text"
                :init="tinymceInit"
                class="builder-view__wysiwyg"
                @input="activeBlock.text = $event"
              />
            </div>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'ideaSection'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">IDEA SECTION</h4>
            <div class="builder-view__field">
              <label class="builder-view__label">Title</label>
              <textarea
                v-model="activeBlock.title"
                class="builder-view__textarea"
                rows="2"
                placeholder="e.g. Look at some ideas for furnishing"
              />
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Description</label>
              <Editor
                :value="activeBlock.description"
                :init="tinymceInit"
                class="builder-view__wysiwyg"
                @input="activeBlock.description = $event"
              />
            </div>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'cooperateSection'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">COOPERATE SECTION</h4>
            <p class="builder-view__section-hint">Background is fixed. Only title and description are editable.</p>
            <div class="builder-view__field">
              <label class="builder-view__label">Title</label>
              <textarea
                v-model="activeBlock.title"
                class="builder-view__textarea"
                rows="2"
                placeholder="Sarađujte sa nama"
              />
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Description</label>
              <textarea
                v-model="activeBlock.description"
                class="builder-view__textarea"
                rows="3"
                placeholder="Postanite deo naših partnera..."
              />
            </div>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'bestMaterialSection'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">BEST MATERIAL SECTION</h4>
            <p class="builder-view__section-hint">Background is fixed. Only title and description are editable. Title appears on the left, description on the right.</p>
            <div class="builder-view__field">
              <label class="builder-view__label">Title</label>
              <textarea
                v-model="activeBlock.title"
                class="builder-view__textarea"
                rows="2"
                placeholder="Koji materijal najbolji za radnu ploču u kuhinji?"
              />
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Description</label>
              <Editor
                :value="activeBlock.description"
                :init="tinymceInit"
                class="builder-view__wysiwyg"
                @input="activeBlock.description = $event"
              />
            </div>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'materialTiles'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">MATERIAL TILES</h4>
            <div class="builder-view__field">
              <label class="builder-view__label">Select material by category</label>
              <el-select
                v-model="activeBlock.quickFilterCategories"
                multiple
                placeholder="Select categories"
                clearable
                class="builder-view__select builder-view__select--full"
              >
                <el-option
                  v-for="cat in materialCategoryOptions"
                  :key="cat"
                  :label="cat"
                  :value="cat"
                />
              </el-select>
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Limit</label>
              <input
                v-model.number="activeBlock.limit"
                type="number"
                class="builder-view__input"
                min="1"
                placeholder="12"
              >
            </div>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'image'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">IMAGE</h4>
            <div class="builder-view__field">
              <div v-if="activeBlock.preview || activeBlock.image" class="builder-view__image-edit-preview">
                <img :src="activeBlock.preview || activeBlock.image" alt="">
                <button type="button" class="builder-view__gallery-remove builder-view__image-remove" @click="clearImageBlockImage">
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>
              <input
                ref="imageBlockInput"
                type="file"
                accept="image/*"
                class="builder-view__file-hidden"
                @change="onImageBlockFileChange"
              >
              <button
                type="button"
                class="builder-view__btn builder-view__btn--outline"
                @click="$refs.imageBlockInput && $refs.imageBlockInput.click()"
              >
                <span class="material-symbols-outlined">add_photo_alternate</span>
                {{ activeBlock.preview || activeBlock.image ? 'Replace image' : 'Add image' }}
              </button>
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Caption</label>
              <input
                v-model="activeBlock.caption"
                type="text"
                class="builder-view__input"
                placeholder="Optional caption"
              >
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Link</label>
              <input
                v-model="activeBlock.link"
                type="text"
                class="builder-view__input"
                placeholder="https://example.com or /proizvodi/..."
              >
            </div>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'gallery'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">GALLERY IMAGES</h4>
            <div class="builder-view__gallery-edit">
              <div
                v-for="(img, i) in galleryBlockImages"
                :key="i"
                class="builder-view__gallery-edit-item"
              >
                <img :src="img.preview" alt="" class="builder-view__gallery-edit-thumb">
                <input
                  v-model="img.description"
                  type="text"
                  class="builder-view__input builder-view__input--sm"
                  placeholder="Description"
                >
                <button
                  type="button"
                  class="builder-view__gallery-remove"
                  @click="removeGalleryImage(i)"
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>
            </div>
            <input
              ref="galleryInput"
              type="file"
              accept="image/*"
              multiple
              class="builder-view__file-hidden"
              @change="onGalleryFilesChange"
            >
            <button
              type="button"
              class="builder-view__btn builder-view__btn--outline"
              @click="$refs.galleryInput.click()"
            >
              <span class="material-symbols-outlined">add_photo_alternate</span>
              Add images
            </button>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'partnersSection'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">PARTNERS IMAGES</h4>
            <p class="builder-view__section-hint">All images will be shown in a 3-per-row grid with the same dimensions.</p>
            <div class="builder-view__gallery-edit">
              <div
                v-for="(img, i) in partnersSectionBlockImages"
                :key="i"
                class="builder-view__gallery-edit-item"
              >
                <img :src="img.preview" alt="" class="builder-view__gallery-edit-thumb">
                <button
                  type="button"
                  class="builder-view__gallery-remove"
                  @click="removePartnersSectionImage(i)"
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>
            </div>
            <input
              ref="partnersSectionInput"
              type="file"
              accept="image/*"
              multiple
              class="builder-view__file-hidden"
              @change="onPartnersSectionFilesChange"
            >
            <button
              type="button"
              class="builder-view__btn builder-view__btn--outline"
              @click="$refs.partnersSectionInput && $refs.partnersSectionInput.click()"
            >
              <span class="material-symbols-outlined">add_photo_alternate</span>
              Add images
            </button>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'video'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">VIDEO</h4>
            <div class="builder-view__field">
              <label class="builder-view__label">Video URL</label>
              <input
                v-model="activeBlock.url"
                type="url"
                class="builder-view__input"
                placeholder="https://www.youtube.com/watch?v=..."
              >
            </div>
            <div class="builder-view__field">
              <label class="builder-view__label">Caption</label>
              <input
                v-model="activeBlock.caption"
                type="text"
                class="builder-view__input"
                placeholder="Optional caption"
              >
            </div>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'tiles'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">TILES</h4>
            <div class="builder-view__tiles-edit">
              <div
                v-for="(tile, i) in (activeBlock.tiles || [])"
                :key="i"
                class="builder-view__tiles-edit-item"
              >
                <div v-if="tile.preview || tile.image" class="builder-view__tile-edit-preview">
                  <img :src="tile.preview || tile.image" alt="">
                  <button
                    type="button"
                    class="builder-view__gallery-remove builder-view__tile-image-remove"
                    @click="clearTilesItemImage(i)"
                  >
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </div>
                <button
                  v-if="!tile.preview && !tile.image"
                  type="button"
                  class="builder-view__btn builder-view__btn--outline builder-view__tile-add-img"
                  @click="openTilesImagePicker(i)"
                >
                  <span class="material-symbols-outlined">add_photo_alternate</span>
                  Add image
                </button>
                <button
                  v-else
                  type="button"
                  class="builder-view__btn builder-view__btn--outline builder-view__tile-add-img"
                  @click="openTilesImagePicker(i)"
                >
                  <span class="material-symbols-outlined">add_photo_alternate</span>
                  Replace image
                </button>
                <div class="builder-view__field">
                  <label class="builder-view__label">Title</label>
                  <input
                    v-model="tile.title"
                    type="text"
                    class="builder-view__input builder-view__input--sm"
                    placeholder="Tile title"
                  >
                </div>
                <div class="builder-view__field">
                  <label class="builder-view__label">Link ({{ selectedLang.toUpperCase() }})</label>
                  <input
                    v-model="tile.link"
                    type="text"
                    class="builder-view__input builder-view__input--sm"
                    :placeholder="selectedLang === 'en' ? '/en/quartz-worktops' : '/kvarcne-ploce'"
                  >
                </div>
                <button
                  type="button"
                  class="builder-view__gallery-remove builder-view__tile-remove"
                  @click="removeTilesItem(i)"
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>
            </div>
            <input
              ref="tilesImageInput"
              type="file"
              accept="image/*"
              class="builder-view__file-hidden"
              @change="onTilesItemFileChange"
            >
            <button
              type="button"
              class="builder-view__btn builder-view__btn--outline"
              @click="addTilesItem"
            >
              <span class="material-symbols-outlined">add</span>
              Add tile
            </button>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'videoGallery'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">VIDEOS</h4>
            <div class="builder-view__video-gallery-edit">
              <div
                v-for="(v, i) in (activeBlock.videos || [])"
                :key="i"
                class="builder-view__video-gallery-edit-item"
              >
                <input
                  v-model="v.url"
                  type="url"
                  class="builder-view__input builder-view__input--sm"
                  placeholder="Video URL"
                >
                <button
                  type="button"
                  class="builder-view__gallery-remove"
                  @click="removeVideoGalleryItem(i)"
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>
            </div>
            <button
              type="button"
              class="builder-view__btn builder-view__btn--outline"
              @click="addVideoGalleryItem"
            >
              <span class="material-symbols-outlined">add</span>
              Add video
            </button>
          </section>
        </div>

        <div v-else-if="activeBlock.type === 'faq'" class="builder-view__sidebar-body">
          <section class="builder-view__section">
            <h4 class="builder-view__section-title">FAQ</h4>
            <div class="builder-view__field">
              <label class="builder-view__label">Title</label>
              <textarea
                v-model="activeBlock.title"
                class="builder-view__textarea"
                rows="2"
                placeholder="Pitanja i odgovori (FAQ)"
              />
            </div>
            <div class="builder-view__faq-edit">
              <div
                v-for="(q, i) in (activeBlock.questions || [])"
                :key="i"
                class="builder-view__faq-edit-item"
              >
                <div class="builder-view__field">
                  <label class="builder-view__label">Question</label>
                  <input
                    v-model="q.title"
                    type="text"
                    class="builder-view__input"
                    placeholder="Question text"
                  >
                </div>
                <div class="builder-view__field">
                  <label class="builder-view__label">Answer</label>
                  <Editor
                    :value="q.text"
                    :init="tinymceInit"
                    class="builder-view__wysiwyg"
                    @input="q.text = $event"
                  />
                </div>
                <button
                  type="button"
                  class="builder-view__gallery-remove builder-view__faq-remove"
                  @click="removeFaqQuestion(i)"
                >
                  <span class="material-symbols-outlined">close</span>
                  Remove question
                </button>
              </div>
            </div>
            <button
              type="button"
              class="builder-view__btn builder-view__btn--outline"
              @click="addFaqQuestion"
            >
              <span class="material-symbols-outlined">add</span>
              Add question
            </button>
          </section>
        </div>

        <button v-if="activeBlock && !appendBlockMode" type="button" class="builder-view__save-props" :disabled="saving" @click="saveAndClosePanel">
          Save Properties
        </button>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { Loading, Message, Select, Option } from 'element-ui'
import 'tinymce/tinymce'
import Editor from '@tinymce/tinymce-vue'

// Fonts available on the public site (mondial-ssr/assets/style/fonts.scss)
const SITE_FONT_FORMATS = [
  "PT Serif='PT Serif', serif",
  "Open Sans='Open Sans', sans-serif",
  'Oswald=Oswald, sans-serif',
  'Caveat=Caveat, cursive'
].join(';')

const SITE_FONT_SIZES = '12px 14px 16px 18px 20px 24px 28px 32px 40px 48px 64px'

const SITE_COLOR_MAP = [
  '000000', 'Crna',
  '333333', 'Tamno siva',
  '777777', 'Siva',
  'CCCCCC', 'Svetlo siva',
  'FFFFFF', 'Bela',
  'B99B6B', 'Zlatna',
  'C0392B', 'Crvena',
  '2980B9', 'Plava',
  '27AE60', 'Zelena'
]

// Loaded inside the editor iframe so previews match the public site
const SITE_FONTS_IMPORT = "@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400..700&family=Open+Sans:ital,wght@0,300..800;1,300..800&family=Oswald:wght@200..700&family=PT+Serif:ital,wght@0,400;0,700;1,400;1,700&display=swap');"

const slug = (value) => {
  if (value == null || value === '') return false
  return /^[a-z0-9](-?[a-z0-9])*(\/[a-z0-9](-?[a-z0-9])*)*$/.test(String(value).trim())
}

function parseVideoEmbedUrl (url) {
  if (!url || typeof url !== 'string') return ''
  const trimmed = url.trim()
  let m = trimmed.match(/youtube\.com\/watch\?v=([^&]+)/)
  if (m) return `https://www.youtube.com/embed/${m[1]}`
  m = trimmed.match(/youtu\.be\/([^?]+)/)
  if (m) return `https://www.youtube.com/embed/${m[1]}`
  m = trimmed.match(/vimeo\.com\/(\d+)/)
  if (m) return `https://player.vimeo.com/video/${m[1]}`
  return ''
}

function createTextBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'text',
    headline: '',
    description: '',
    darkBackground: false,
    paddingY: 120,
    maxWidth: '',
    textAlign: 'center'
  }
}

function createImageBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'image',
    image: null,
    caption: '',
    link: ''
  }
}

function createGalleryBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'gallery',
    images: []
  }
}

function createVideoBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'video',
    url: '',
    caption: ''
  }
}

function createVideoGalleryBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'videoGallery',
    videos: []
  }
}

function createTilesBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'tiles',
    tiles: []
  }
}

function createGreySectionBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'greySection',
    title: '',
    subtitle: '',
    text: ''
  }
}

function createIdeaSectionBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'ideaSection',
    title: '',
    description: ''
  }
}

function createCooperateSectionBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'cooperateSection',
    title: 'Sarađujte sa nama',
    description: 'Postanite deo naših partnera i saznajte na koji način da dođete do kvalitetnih proizvoda od kamena.'
  }
}

function createBestMaterialSectionBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'bestMaterialSection',
    title: '',
    description: ''
  }
}

function createFaqBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'faq',
    title: '',
    questions: []
  }
}

function cloneBlockForOtherLang (block) {
  if (block.type === 'faq') {
    return {
      ...block,
      title: block.title != null ? block.title : '',
      questions: (block.questions || []).map(q => ({
        title: (q && q.title) != null ? q.title : '',
        text: (q && q.text) != null ? q.text : ''
      }))
    }
  }
  return { ...block }
}

function createPartnersSectionBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'partnersSection',
    images: []
  }
}

function createMaterialTilesBlock () {
  return {
    id: 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    type: 'materialTiles',
    materialIds: [],
    quickFilterCategories: [],
    limit: 12
  }
}

export default {
  name: 'ManagePage',

  components: {
    Editor,
    ElSelect: Select,
    ElOption: Option
  },

  mixins: [validationMixin],

  beforeRouteEnter (to, from, next) {
    const promises = [
      axios.get('/api/pages'),
      axios.get('/api/galleries')
    ]
    if (to.name === 'EditPage') promises.push(axios.get(`/api/page/${to.params.id}`))
    Promise.all(promises)
      .then(response => {
        next(vm => {
          vm.pages = (response[0] && response[0].data && response[0].data.data) || []
          vm.galleries = (response[1] && response[1].data && response[1].data.data) || []
          vm.edit = (response[2] && response[2].data && response[2].data.data) || null
          vm.init()
        })
      })
      .catch(() => {
        next(vm => {
          Message.error('Nije moguće učitati podatke. Proverite konekciju sa serverom.')
          vm.$router.push({ name: 'Pages' })
        })
      })
  },

  data () {
    return {
      tinymceInit: {
        base_url: (process.env.BASE_URL || '/') + 'tinymce',
        height: 220,
        menubar: false,
        plugins: 'lists link',
        toolbar: 'fontselect fontsizeselect forecolor | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist | link',
        font_formats: SITE_FONT_FORMATS,
        fontsize_formats: SITE_FONT_SIZES,
        color_map: SITE_COLOR_MAP,
        content_style: SITE_FONTS_IMPORT + ' body { font-family: \'Open Sans\', sans-serif; font-size: 14px; background: #fff; }'
      },
      selectedLang: 'rs',
      form: {
        image: null,
        parent: null,
        footerProducts: false,
        data: {
          en: {
            title: null,
            meta: null,
            keywords: [],
            heroImage: null,
            blocks: []
          },
          rs: {
            title: null,
            meta: null,
            keywords: [],
            heroImage: null,
            blocks: []
          }
        },
        images: []
      },
      url: { en: null, rs: null },
      pages: [],
      langs: { rs: 'Srpski', en: 'Engleski' },
      edit: null,
      galleries: [],
      activeBlockId: null,
      appendBlockMode: false,
      draggedBlockIndex: null,
      newPageKeyword: '',
      tilesImagePickerIndex: null,
      saving: false,
      uploadingCount: 0
    }
  },

  computed: {
    ...mapGetters('materijali', ['materials']),

    pageKeywords () {
      const kw = this.form.data[this.selectedLang].keywords
      return Array.isArray(kw) ? kw : []
    },

    pageHeroImageDisplay () {
      const hero = this.form.data[this.selectedLang].heroImage
      if (!hero || typeof hero !== 'string') return ''
      if (hero.startsWith('data:') || hero.startsWith('http') || hero.startsWith('blob:')) return hero
      return (this.$path || '') + (hero.startsWith('/') ? hero : '/' + hero)
    },

    urlPrefix () {
      let url = 'https://mondial90.rs'
      if (process.env.NODE_ENV === 'development' && process.env.VUE_APP_LOCAL_FRONTEND) {
        url = process.env.VUE_APP_LOCAL_FRONTEND
      }
      const suffix = this.selectedLang === 'en' ? '/en' : ''
      return url + suffix
    },

    isEdit () {
      return this.$route.name === 'EditPage'
    },

    blocks () {
      return this.form.data[this.selectedLang].blocks || []
    },

    activeBlock () {
      if (!this.activeBlockId) return null
      return this.blocks.find(b => b.id === this.activeBlockId) || null
    },

    galleryBlockImages () {
      const block = this.activeBlock
      if (!block || block.type !== 'gallery') return []
      return block.images || []
    },

    partnersSectionBlockImages () {
      const block = this.activeBlock
      if (!block || block.type !== 'partnersSection') return []
      return block.images || []
    },

    materialCategoryOptions () {
      const CATEGORY_OPTIONS = []
      const fromData = (this.materials || []).reduce((acc, m) => {
        const cats = Array.isArray(m.materialCategories) ? m.materialCategories : []
        cats.forEach((c) => { if (c && !acc.includes(c)) acc.push(c) })
        return acc
      }, [])
      const combined = [...CATEGORY_OPTIONS]
      fromData.forEach((c) => { if (c && !combined.includes(c)) combined.push(c) })
      return combined.length ? combined : CATEGORY_OPTIONS
    },

    previewUrl () {
      const frontendBase = process.env.NODE_ENV === 'development' && process.env.VUE_APP_LOCAL_FRONTEND
        ? process.env.VUE_APP_LOCAL_FRONTEND
        : 'https://mondial90.rs'
      const segment = this.selectedLang === 'rs' ? this.url.rs : this.url.en
      if (!segment || typeof segment !== 'string' || !segment.trim()) return ''
      const slug = segment.replace(/^\//, '')
      const path = this.selectedLang === 'en' ? 'en/' + slug : slug
      return frontendBase + '/' + path
    }
  },

  validations: {
    url: {
      rs: { required, slug },
      en: { required, slug }
    },
    form: {
      data: {
        en: { title: { required } },
        rs: { title: { required } }
      }
    }
  },

  watch: {
    selectedLang () {
      this.newPageKeyword = ''
    }
  },

  methods: {
    blockTypeLabel (type) {
      const labels = { text: 'Text Section', image: 'Image Section', gallery: 'Gallery', video: 'Video Embed', videoGallery: 'Video Gallery', tiles: 'Tiles Section', greySection: 'Grey section', ideaSection: 'Idea section', cooperateSection: 'Cooperate section', bestMaterialSection: 'Best Material Section', faq: 'FAQ', partnersSection: 'Partners section', materialTiles: 'Material tiles' }
      return labels[type] || type
    },

    videoEmbedUrl (url) {
      return parseVideoEmbedUrl(url)
    },

    blockImages (block) {
      if (block.type !== 'gallery' || !block.images) return []
      return block.images.map(img => ({
        preview: img.image || img.preview || ''
      }))
    },

    partnersBlockImages (block) {
      if (block.type !== 'partnersSection' || !block.images) return []
      return block.images.map(img => ({
        preview: img.image || img.preview || ''
      }))
    },

    placeholderCount (block) {
      if (block.type !== 'gallery') return 0
      const len = (block.images || []).length
      return Math.max(0, 3 - len)
    },

    partnersPlaceholderCount (block) {
      if (block.type !== 'partnersSection') return 0
      const len = (block.images || []).length
      return Math.max(0, 6 - len)
    },

    videoGalleryPlaceholderCount (block) {
      if (block.type !== 'videoGallery') return 0
      const len = (block.videos || []).length
      return Math.max(0, 3 - len)
    },

    tilesPlaceholderCount (block) {
      if (block.type !== 'tiles') return 0
      const len = (block.tiles || []).length
      return Math.max(0, 3 - len)
    },

    selectBlock (block) {
      this.activeBlockId = block.id
    },

    addBlock (type) {
      let block
      if (type === 'text') block = createTextBlock()
      else if (type === 'image') block = createImageBlock()
      else if (type === 'gallery') block = createGalleryBlock()
      else if (type === 'video') block = createVideoBlock()
      else if (type === 'videoGallery') block = createVideoGalleryBlock()
      else if (type === 'tiles') block = createTilesBlock()
      else if (type === 'greySection') block = createGreySectionBlock()
      else if (type === 'ideaSection') block = createIdeaSectionBlock()
      else if (type === 'cooperateSection') block = createCooperateSectionBlock()
      else if (type === 'bestMaterialSection') block = createBestMaterialSectionBlock()
      else if (type === 'faq') block = createFaqBlock()
      else if (type === 'partnersSection') block = createPartnersSectionBlock()
      else if (type === 'materialTiles') block = createMaterialTilesBlock()
      else return
      const otherLang = this.selectedLang === 'rs' ? 'en' : 'rs'
      this.form.data[this.selectedLang].blocks.push(block)
      this.form.data[otherLang].blocks.push(cloneBlockForOtherLang(block))
      this.activeBlockId = block.id
      this.appendBlockMode = false
    },

    ideaSectionTitles () {
      const list = this.galleries || []
      const isEn = this.selectedLang === 'en'
      return list.map((g) => ({
        id: g.id,
        title: isEn ? (g.name_en || g.name || '') : (g.name || '')
      }))
    },

    materialTilesPreviewMaterials (block) {
      if (!block || block.type !== 'materialTiles') return []
      const materials = this.materials || []
      const cats = Array.isArray(block.quickFilterCategories) ? block.quickFilterCategories : []
      const limit = typeof block.limit === 'number' && block.limit > 0 ? block.limit : 12

      const filtered =
        cats.length > 0
          ? materials.filter((m) => {
            const mCats = Array.isArray(m.materialCategories) ? m.materialCategories : []
            return cats.some((c) => mCats.includes(c))
          })
          : materials

      return filtered.slice(0, limit)
    },

    materialTileImageStyle (material) {
      const img = material.squareTile || material.square_tile
      if (img) {
        return {
          backgroundImage: `url(${img})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#f1f5f9'
        }
      }
      return { background: 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)' }
    },

    removeBlock (index) {
      const block = this.blocks[index]
      const wasActive = block && this.activeBlockId === block.id
      const otherLang = this.selectedLang === 'rs' ? 'en' : 'rs'
      const otherIndex = (this.form.data[otherLang].blocks || []).findIndex(b => b.id === block.id)
      this.form.data[this.selectedLang].blocks.splice(index, 1)
      if (otherIndex !== -1) this.form.data[otherLang].blocks.splice(otherIndex, 1)
      if (wasActive) this.activeBlockId = null
    },

    onBlockDragStart (e, index) {
      this.draggedBlockIndex = index
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(index))
    },

    onBlockDragEnd () {
      this.draggedBlockIndex = null
    },

    onBlockDragOver (e, index) {
      if (this.draggedBlockIndex === null) return
      e.dataTransfer.dropEffect = 'move'
    },

    onBlockDrop (dropIndex) {
      if (this.draggedBlockIndex === null || this.draggedBlockIndex === dropIndex) return
      const blocks = this.form.data[this.selectedLang].blocks
      const [moved] = blocks.splice(this.draggedBlockIndex, 1)
      blocks.splice(dropIndex, 0, moved)
      const otherLang = this.selectedLang === 'rs' ? 'en' : 'rs'
      const otherBlocks = this.form.data[otherLang].blocks
      if (otherBlocks && otherBlocks.length) {
        const orderedIds = blocks.map(b => b.id)
        this.form.data[otherLang].blocks = orderedIds
          .map(id => otherBlocks.find(b => b.id === id))
          .filter(Boolean)
          .concat(otherBlocks.filter(b => !orderedIds.includes(b.id)))
      }
      this.draggedBlockIndex = null
    },

    addPageKeyword () {
      const kw = (this.newPageKeyword || '').trim()
      if (!kw) return
      if (!this.form.data[this.selectedLang].keywords) {
        this.form.data[this.selectedLang].keywords = []
      }
      this.form.data[this.selectedLang].keywords.push(kw)
      this.newPageKeyword = ''
    },

    removePageKeyword (index) {
      const kw = this.form.data[this.selectedLang].keywords
      if (Array.isArray(kw)) kw.splice(index, 1)
    },

    removeGalleryImage (index) {
      const block = this.activeBlock
      if (block && block.type === 'gallery' && block.images) {
        block.images.splice(index, 1)
        this.syncBlockNonText(block.id)
      }
    },

    removePartnersSectionImage (index) {
      const block = this.activeBlock
      if (block && block.type === 'partnersSection' && block.images) {
        block.images.splice(index, 1)
        this.syncBlockNonText(block.id)
      }
    },

    addVideoGalleryItem () {
      const block = this.activeBlock
      if (block && block.type === 'videoGallery') {
        if (!block.videos) block.videos = []
        block.videos.push({ url: '' })
        this.syncBlockNonText(block.id)
      }
    },

    removeVideoGalleryItem (index) {
      const block = this.activeBlock
      if (block && block.type === 'videoGallery' && block.videos) {
        block.videos.splice(index, 1)
        this.syncBlockNonText(block.id)
      }
    },

    addFaqQuestion () {
      const block = this.activeBlock
      if (block && block.type === 'faq') {
        if (!block.questions) block.questions = []
        block.questions.push({ title: '', text: '' })
      }
    },

    removeFaqQuestion (index) {
      const block = this.activeBlock
      if (block && block.type === 'faq' && block.questions) {
        block.questions.splice(index, 1)
      }
    },

    addTilesItem () {
      const block = this.activeBlock
      if (block && block.type === 'tiles') {
        if (!block.tiles) block.tiles = []
        block.tiles.push({ title: '', link: '' })
        this.syncBlockNonText(block.id)
      }
    },

    removeTilesItem (index) {
      const block = this.activeBlock
      if (block && block.type === 'tiles' && block.tiles) {
        block.tiles.splice(index, 1)
        this.syncBlockNonText(block.id)
      }
    },

    openTilesImagePicker (index) {
      this.tilesImagePickerIndex = index
      this.$nextTick(() => {
        if (this.$refs.tilesImageInput) {
          this.$refs.tilesImageInput.click()
        }
      })
    },

    async uploadImage (file) {
      const form = new FormData()
      form.append('image', file)
      const { data } = await axios.post('/api/gallery/upload', form)
      if (!data || !data.path) throw new Error('Upload failed')
      return data.path
    },

    async onTilesItemFileChange (e) {
      const files = e.target.files
      if (!files || !files.length) return
      const block = this.activeBlock
      if (!block || block.type !== 'tiles' || !block.tiles) return
      const index = this.tilesImagePickerIndex
      if (index == null || index >= block.tiles.length) return
      const file = files[0]
      this.tilesImagePickerIndex = null
      e.target.value = ''
      const previewUrl = URL.createObjectURL(file)
      this.$set(block.tiles[index], 'image', previewUrl)
      this.$set(block.tiles[index], 'preview', previewUrl)
      this.syncBlockNonText(block.id)
      this.uploadingCount++
      try {
        const path = await this.uploadImage(file)
        this.$set(block.tiles[index], 'image', path)
        this.$set(block.tiles[index], 'path', path)
        this.$set(block.tiles[index], 'preview', (this.$path || '') + (path.startsWith('/') ? path : '/' + path))
        this.syncBlockNonText(block.id)
      } catch (err) {
        this.$set(block.tiles[index], 'image', null)
        this.$set(block.tiles[index], 'path', null)
        this.$set(block.tiles[index], 'preview', null)
        this.syncBlockNonText(block.id)
        Message.error('Tile image upload failed.')
      } finally {
        this.uploadingCount--
        URL.revokeObjectURL(previewUrl)
      }
    },

    clearTilesItemImage (index) {
      const block = this.activeBlock
      if (block && block.type === 'tiles' && block.tiles && block.tiles[index]) {
        block.tiles[index].image = null
        block.tiles[index].preview = null
        this.$set(block.tiles[index], 'path', null)
        this.syncBlockNonText(block.id)
      }
    },

    clearImageBlockImage () {
      const block = this.activeBlock
      if (block && block.type === 'image') {
        block.image = null
        block.preview = null
        this.syncBlockNonText(block.id)
      }
    },

    clearHeroImage () {
      this.form.data.rs.heroImage = null
      this.form.data.en.heroImage = null
      if (this.$refs.heroImageInput) this.$refs.heroImageInput.value = ''
    },

    async onHeroImageFileChange (e) {
      const files = e.target.files
      if (!files || !files.length) return
      const file = files[0]
      e.target.value = ''
      const previewUrl = URL.createObjectURL(file)
      this.form.data.rs.heroImage = previewUrl
      this.form.data.en.heroImage = previewUrl
      this.uploadingCount++
      try {
        const path = await this.uploadImage(file)
        this.form.data.rs.heroImage = path
        this.form.data.en.heroImage = path
      } catch (err) {
        this.form.data.rs.heroImage = null
        this.form.data.en.heroImage = null
        Message.error('Hero image upload failed.')
      } finally {
        this.uploadingCount--
        URL.revokeObjectURL(previewUrl)
      }
    },

    async onImageBlockFileChange (e) {
      const files = e.target.files
      if (!files || !files.length) return
      const block = this.activeBlock
      if (!block || block.type !== 'image') return
      const file = files[0]
      e.target.value = ''
      const previewUrl = URL.createObjectURL(file)
      block.image = previewUrl
      block.preview = previewUrl
      this.syncBlockNonText(block.id)
      this.uploadingCount++
      try {
        const path = await this.uploadImage(file)
        block.image = path
        block.preview = (this.$path || '') + (path.startsWith('/') ? path : '/' + path)
        this.syncBlockNonText(block.id)
      } catch (err) {
        block.image = null
        block.preview = null
        this.syncBlockNonText(block.id)
        Message.error('Image upload failed.')
      } finally {
        this.uploadingCount--
        URL.revokeObjectURL(previewUrl)
      }
    },

    async onGalleryFilesChange (e) {
      const files = e.target.files
      if (!files || !files.length) return
      const block = this.activeBlock
      if (!block || block.type !== 'gallery') return
      if (!block.images) block.images = []
      e.target.value = ''
      const fileList = Array.from(files)
      const entries = fileList.map(file => {
        const previewUrl = URL.createObjectURL(file)
        const entry = { image: previewUrl, preview: previewUrl, description: '' }
        block.images.push(entry)
        return { file, entry, previewUrl }
      })
      this.syncBlockNonText(block.id)
      for (const { file, entry, previewUrl } of entries) {
        this.uploadingCount++
        try {
          const path = await this.uploadImage(file)
          entry.image = path
          entry.preview = (this.$path || '') + (path.startsWith('/') ? path : '/' + path)
          entry.path = path
        } catch (err) {
          const idx = block.images.indexOf(entry)
          if (idx !== -1) block.images.splice(idx, 1)
          Message.error('Failed to upload a gallery image.')
        } finally {
          this.uploadingCount--
          URL.revokeObjectURL(previewUrl)
        }
      }
      this.syncBlockNonText(block.id)
    },

    async onPartnersSectionFilesChange (e) {
      const files = e.target.files
      if (!files || !files.length) return
      const block = this.activeBlock
      if (!block || block.type !== 'partnersSection') return
      if (!block.images) block.images = []
      e.target.value = ''
      const fileList = Array.from(files)
      const entries = fileList.map(file => {
        const previewUrl = URL.createObjectURL(file)
        const entry = { image: previewUrl, preview: previewUrl }
        block.images.push(entry)
        return { file, entry, previewUrl }
      })
      this.syncBlockNonText(block.id)
      for (const { file, entry, previewUrl } of entries) {
        this.uploadingCount++
        try {
          const path = await this.uploadImage(file)
          entry.image = path
          entry.preview = (this.$path || '') + (path.startsWith('/') ? path : '/' + path)
          entry.path = path
        } catch (err) {
          const idx = block.images.indexOf(entry)
          if (idx !== -1) block.images.splice(idx, 1)
          Message.error('Failed to upload a partner image.')
        } finally {
          this.uploadingCount--
          URL.revokeObjectURL(previewUrl)
        }
      }
      this.syncBlockNonText(block.id)
    },

    init () {
      this.$store.dispatch('materijali/load').catch(() => {})
      if (this.edit) {
        this.url.rs = this.edit.url != null ? this.edit.url : ''
        this.url.en = this.edit.urlEN != null ? this.edit.urlEN : ''
        const data = this.edit.data || {}
        const rs = data.rs || {}
        const en = data.en || {}

        if (rs.blocks && rs.blocks.length) {
          const normalizedRs = rs.blocks.map(b => this.normalizeBlock(b))
          const heroBlockRs = normalizedRs.find(b => b.type === 'heroSection' && (b.image || b.preview))
          this.form.data.rs = {
            title: rs.title,
            meta: rs.meta,
            keywords: Array.isArray(rs.keywords) ? rs.keywords.slice() : [],
            heroImage: heroBlockRs ? (heroBlockRs.image || heroBlockRs.preview) : null,
            blocks: normalizedRs.filter(b => b.type !== 'heroSection')
          }
        } else {
          const migratedRs = this.migrateToBlocks(rs, (this.edit.images || []).map(img => ({
            path: img.image,
            image: this.$path + img.image,
            description: img.description || ''
          })))
          const heroBlockRs = migratedRs.find(b => b.type === 'heroSection' && (b.image || b.preview))
          this.form.data.rs = {
            title: rs.title,
            meta: rs.meta,
            keywords: Array.isArray(rs.keywords) ? rs.keywords.slice() : [],
            heroImage: heroBlockRs ? (heroBlockRs.image || heroBlockRs.preview) : null,
            blocks: migratedRs.filter(b => b.type !== 'heroSection')
          }
        }

        if (en.blocks && en.blocks.length) {
          const normalizedEn = en.blocks.map(b => this.normalizeBlock(b))
          const heroBlockEn = normalizedEn.find(b => b.type === 'heroSection' && (b.image || b.preview))
          this.form.data.en = {
            title: en.title,
            meta: en.meta,
            keywords: Array.isArray(en.keywords) ? en.keywords.slice() : [],
            heroImage: heroBlockEn ? (heroBlockEn.image || heroBlockEn.preview) : null,
            blocks: normalizedEn.filter(b => b.type !== 'heroSection')
          }
        } else {
          const migratedEn = this.migrateToBlocks(en, (this.edit.images || []).map(img => ({
            path: img.image,
            image: this.$path + img.image,
            description: img.description || ''
          })))
          const heroBlockEn = migratedEn.find(b => b.type === 'heroSection' && (b.image || b.preview))
          this.form.data.en = {
            title: en.title,
            meta: en.meta,
            keywords: Array.isArray(en.keywords) ? en.keywords.slice() : [],
            heroImage: heroBlockEn ? (heroBlockEn.image || heroBlockEn.preview) : null,
            blocks: migratedEn.filter(b => b.type !== 'heroSection')
          }
        }

        this.form.image = this.edit.image
        this.form.parent = this.edit.parent
        this.form.footerProducts = !!(this.edit.data && this.edit.data.footerProducts)
      } else {
        this.url.rs = ''
        this.url.en = ''
        const initialBlock = createTextBlock()
        this.form.data.rs.blocks = [{ ...initialBlock }]
        this.form.data.en.blocks = [{ ...initialBlock }]
      }
    },

    normalizeBlock (b) {
      const block = { ...b }
      if (!block.id) block.id = 'b-' + Date.now() + '-' + Math.random().toString(36).slice(2)
      if (block.type === 'image') {
        if (!block.caption) block.caption = ''
        if (block.link == null) block.link = ''
        if (block.image) this.resolveBlockImageUrl(block)
      }
      if (block.type === 'gallery' && block.images) {
        block.images = block.images.map(img => {
          const next = typeof img === 'string' ? { image: img, description: '' } : { ...img }
          this.resolveBlockImageUrl(next)
          return next
        })
      }
      if (block.type === 'videoGallery' && block.videos) {
        block.videos = block.videos.map(v => (typeof v === 'string' ? { url: v } : { ...v }))
      }
      if (block.type === 'tiles' && block.tiles) {
        block.tiles = block.tiles.map(t => {
          const tile = typeof t === 'string' ? { title: '', link: '', image: t } : { title: '', link: '', ...t }
          if (tile.image) this.resolveBlockImageUrl(tile)
          return tile
        })
      }
      if (block.type === 'greySection') {
        if (block.title == null) block.title = ''
        if (block.subtitle == null) block.subtitle = ''
        if (block.text == null) block.text = ''
      }
      if (block.type === 'ideaSection') {
        if (block.title == null) block.title = ''
        if (block.description == null) block.description = ''
      }
      if (block.type === 'cooperateSection') {
        if (block.title == null) block.title = ''
        if (block.description == null) block.description = ''
      }
      if (block.type === 'heroSection') {
        if (block.image == null) block.image = null
        if (block.preview == null) block.preview = null
        if (block.image) this.resolveBlockImageUrl(block)
      }
      if (block.type === 'faq') {
        if (block.title == null) block.title = ''
        if (!Array.isArray(block.questions)) block.questions = []
        block.questions = block.questions.map(q => ({
          title: (q && q.title) != null ? q.title : '',
          text: (q && q.text) != null ? q.text : ''
        }))
      }
      if (block.type === 'partnersSection' && block.images) {
        block.images = block.images.map(img => {
          const next = typeof img === 'string' ? { image: img, preview: img } : { ...img }
          this.resolveBlockImageUrl(next)
          return next
        })
      }
      if (block.type === 'materialTiles') {
        if (!Array.isArray(block.materialIds)) block.materialIds = []
        if (!Array.isArray(block.quickFilterCategories)) block.quickFilterCategories = []
        if (block.limit == null || typeof block.limit !== 'number') block.limit = 12
      }
      return block
    },

    resolveBlockImageUrl (next) {
      const path = next.path || next.image || next.preview
      if (!path || typeof path !== 'string') return
      if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('blob:')) return
      const base = path.startsWith('/') ? path : '/' + path
      const full = this.$path + base
      next.image = full
      next.preview = full
      if (!next.path) next.path = path
    },

    migrateToBlocks (langData, images) {
      const blocks = []
      if (langData.h1 || langData.content) {
        blocks.push({
          ...createTextBlock(),
          headline: langData.h1 || '',
          description: langData.content || ''
        })
      }
      if (images.length) {
        blocks.push({
          ...createGalleryBlock(),
          images: images.map(img => ({
            path: img.path,
            image: img.image,
            preview: img.image,
            description: img.description || ''
          }))
        })
      }
      if (!blocks.length) blocks.push(createTextBlock())
      return blocks
    },

    _applyNonTextSync (src, tgt) {
      if (src.type !== tgt.type) return
      if (src.type === 'text') {
        tgt.paddingY = src.paddingY
        tgt.maxWidth = src.maxWidth
        tgt.textAlign = src.textAlign
        tgt.darkBackground = src.darkBackground
      } else if (src.type === 'image') {
        tgt.image = src.image
        tgt.preview = src.preview
        tgt.link = src.link
      } else if (src.type === 'gallery') {
        const srcImages = src.images || []
        const tgtImages = tgt.images || []
        tgt.images = srcImages.map((img, i) => ({
          ...img,
          description: tgtImages[i] !== undefined ? tgtImages[i].description : ''
        }))
      } else if (src.type === 'partnersSection') {
        tgt.images = (src.images || []).map(img => ({ ...img }))
      } else if (src.type === 'video') {
        tgt.url = src.url
      } else if (src.type === 'tiles') {
        const srcTiles = src.tiles || []
        const tgtTiles = tgt.tiles || []
        tgt.tiles = srcTiles.map((tile, i) => ({
          ...tile,
          title: tgtTiles[i] !== undefined ? tgtTiles[i].title : '',
          link: tgtTiles[i] !== undefined ? (tgtTiles[i].link || '') : ''
        }))
      } else if (src.type === 'videoGallery') {
        tgt.videos = (src.videos || []).map(v => ({ ...v }))
      } else if (src.type === 'materialTiles') {
        tgt.materialIds = (src.materialIds || []).slice()
        tgt.quickFilterCategories = (src.quickFilterCategories || []).slice()
        tgt.limit = src.limit
      }
    },

    syncBlockNonText (blockId) {
      const otherLang = this.selectedLang === 'rs' ? 'en' : 'rs'
      const src = (this.form.data[this.selectedLang].blocks || []).find(b => b.id === blockId)
      const tgt = (this.form.data[otherLang].blocks || []).find(b => b.id === blockId)
      if (src && tgt) this._applyNonTextSync(src, tgt)
    },

    setTextAlign (value) {
      if (!this.activeBlock) return
      this.activeBlock.textAlign = value
      this.syncBlockNonText(this.activeBlock.id)
    },
    textBlockPreviewStyle (block) {
      const style = { textAlign: block.textAlign || 'left' }
      const maxW = (block.maxWidth || '').trim()
      if (maxW) {
        style.maxWidth = maxW
        style.marginLeft = 'auto'
        style.marginRight = 'auto'
      }
      return style
    },

    extractImagePath (url) {
      if (!url || typeof url !== 'string') return null
      if (url.startsWith('blob:')) return null
      let path = url
      if (url.startsWith('http')) {
        try { path = new URL(url).pathname } catch (e) { return url }
      }
      return this.stripAssetBase(path)
    },

    // Display URLs are prefixed with $path (e.g. '/api'); storage always keeps '/uploads/...'
    stripAssetBase (path) {
      const base = (this.$path || '').replace(/\/$/, '')
      if (base && base !== '/' && path.indexOf(base + '/') === 0) {
        return path.slice(base.length)
      }
      return path
    },

    cleanBlock (b) {
      const block = { ...b }
      delete block.preview
      delete block.file
      if (block.type === 'image') {
        block.image = this.extractImagePath(block.image)
      }
      if (block.type === 'gallery' && block.images) {
        block.images = block.images.map(img => ({
          image: this.extractImagePath(img.image) || img.path || null,
          description: img.description || ''
        }))
      }
      if (block.type === 'tiles' && block.tiles) {
        block.tiles = block.tiles.map(t => ({
          title: t.title || '',
          link: t.link || '',
          image: this.extractImagePath(t.image) || t.path || null
        }))
      }
      if (block.type === 'partnersSection' && block.images) {
        block.images = block.images.map(img => ({
          image: this.extractImagePath(img.image) || img.path || null
        }))
      }
      return block
    },

    buildPayload () {
      // Final sync: ensure non-text properties match between languages
      const srcLang = this.selectedLang
      const tgtLang = srcLang === 'rs' ? 'en' : 'rs'
      ;(this.form.data[srcLang].blocks || []).forEach(srcBlock => {
        const tgtBlock = (this.form.data[tgtLang].blocks || []).find(b => b.id === srcBlock.id)
        if (tgtBlock) this._applyNonTextSync(srcBlock, tgtBlock)
      })

      const payload = new FormData()
      payload.append('url', this.url.rs)
      payload.append('urlEN', this.url.en)
      payload.append('parent', this.form.parent || '')
      payload.append('image', this.form.image || '')

      const blocksRs = (this.form.data.rs.blocks || []).filter(b => b.type !== 'heroSection').map(b => this.cleanBlock(b))
      const blocksEn = (this.form.data.en.blocks || []).filter(b => b.type !== 'heroSection').map(b => this.cleanBlock(b))

      const rsHeroPath = this.extractImagePath(this.form.data.rs.heroImage)
      if (rsHeroPath) {
        blocksRs.unshift({ id: 'hero-' + Date.now(), type: 'heroSection', image: rsHeroPath })
      }
      const enHeroPath = this.extractImagePath(this.form.data.en.heroImage)
      if (enHeroPath) {
        blocksEn.unshift({ id: 'hero-en-' + Date.now(), type: 'heroSection', image: enHeroPath })
      }

      const data = {
        footerProducts: !!this.form.footerProducts,
        rs: {
          title: this.form.data.rs.title,
          meta: this.form.data.rs.meta,
          keywords: this.form.data.rs.keywords || [],
          blocks: blocksRs
        },
        en: {
          title: this.form.data.en.title,
          meta: this.form.data.en.meta,
          keywords: this.form.data.en.keywords || [],
          blocks: blocksEn
        }
      }
      payload.append('data', JSON.stringify(data))

      return payload
    },

    openPreview () {
      if (!this.previewUrl) return
      window.open(this.previewUrl, '_blank', 'noopener')
    },

    async save () {
      if (this.saving) return false
      if (this.uploadingCount > 0) {
        Message.warning('Sačekajte da se slike učitaju.')
        return false
      }
      if (this.$v.$invalid) {
        this.$v.$touch()
        Message.warning('Popravite greške u formi pre čuvanja (naslov i URL za oba jezika).')
        return false
      }
      const loading = Loading.service()
      this.saving = true
      try {
        const payload = this.buildPayload()
        const route = this.isEdit ? `/api/page/${this.edit.id}` : '/api/page'
        const response = await axios.post(route, payload)
        const data = response.data
        if (!data || !data.data) {
          Message.error('Server nije vratio podatke stranice. Proverite konzolu i veličinu zahteva.')
          return false
        }
        const createdId = data.data.id
        if (!this.isEdit && (createdId === undefined || createdId === null || createdId === '')) {
          Message.error('Stranica nije sačuvana. Pokušajte ponovo ili proverite konekciju sa serverom.')
          return false
        }
        this.edit = data.data
        this.init()
        Message.success('Stranica uspešno sačuvana.')
        if (!this.isEdit) {
          this.$store.commit('pages/setCreatedPage', data.data)
          this.$router.push({ name: 'Pages' })
        }
        return true
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : 'Greška pri čuvanju.'
        Message.error(msg)
        if (err.response && err.response.status >= 400) {
          console.error('Page save error', err.response.status, err.response.data)
        }
        return false
      } finally {
        this.saving = false
        loading.close()
      }
    },

    async saveAndClosePanel () {
      const saved = await this.save()
      if (saved) {
        this.activeBlockId = null
        this.appendBlockMode = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.builder-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  font-family: 'Manrope', sans-serif;
  background: #f8fafc;
}

.builder-view__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 2rem;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.builder-view__header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.builder-view__back {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: #64748b;
  text-decoration: none;
  transition: color 0.15s;
  .material-symbols-outlined { font-size: 1.25rem; }
  &:hover { color: #0f172a; }
}

.builder-view__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.builder-view__header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.builder-view__lang-toggles--header {
  margin-right: 0.5rem;
}

.builder-view__btn {
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  .material-symbols-outlined { font-size: 1rem; margin-right: 0.25rem; }
  &--secondary {
    background: #f1f5f9;
    color: #64748b;
    &:disabled { opacity: 0.6; cursor: default; }
  }
  &--primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border-radius: 9999px;
    background: #1a1a1a;
    color: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    .material-symbols-outlined { font-size: 0.875rem; margin-right: 0; }
    &:hover:not(:disabled) { background: #334155; }
    &:disabled { opacity: 0.6; cursor: default; }
  }
  &--preview {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border-radius: 9999px;
    background: #fff;
    color: #64748b;
    border: 1px solid #e2e8f0;
    box-shadow: none;
    .material-symbols-outlined { font-size: 0.875rem; margin-right: 0; }
    &:hover:not(:disabled) {
      background: #f8fafc;
      color: #0f172a;
      border-color: #e2e8f0;
    }
    &:disabled { opacity: 0.6; cursor: default; }
  }
  &--outline {
    background: transparent;
    border: 1px solid #e2e8f0;
    color: #64748b;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    &:hover { background: #f8fafc; color: #0f172a; }
  }
  &--sm {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
    margin-top: 0.5rem;
  }
}

.builder-view__select {
  width: 100%;
  &--full { width: 100%; }
}

.builder-view__form-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
  cursor: pointer;
  input[type='checkbox'] { margin: 0; }
}

.builder-view__filter-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.builder-view__filter-btn {
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
  }
  &--active {
    background: #3b82f6;
    border-color: #3b82f6;
    color: #fff;
  }
}

.builder-view__block-material-tiles {
  min-height: 4rem;
}

.builder-view__material-tiles-preview {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 1rem;
}

.builder-view__material-tile-preview {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.builder-view__material-tile-preview-img {
  aspect-ratio: 1;
  width: 100%;
  border-radius: 0.75rem 0.75rem 0 0;
}

.builder-view__material-tile-preview-name {
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #0f172a;
  text-align: center;
}

.builder-view__material-tiles-empty {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  color: #94a3b8;
  font-size: 0.875rem;
  .material-symbols-outlined { font-size: 1.5rem; }
}

.builder-view__body {
  flex: 1;
  display: flex;
  min-height: 0;
}

.builder-view__canvas {
  flex: 2 1 0;
  min-width: 0;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.builder-view__page-settings {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: #fff;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
}

.builder-view__page-row {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
}

.builder-view__page-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
  &--grow { flex: 1; }
  &--equal { flex: 1; }
  &--full { width: 100%; }
}

.builder-view__page-textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  resize: vertical;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #94a3b8; }
}

.builder-view__page-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #94a3b8;
  text-transform: uppercase;
}

.builder-view__page-input {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #fff;
  color: #0f172a;
  outline: none;
  &:focus { border-color: #94a3b8; }
}

.builder-view__keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.builder-view__keyword-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8125rem;
  padding: 0.25rem 0.5rem;
  background: #f1f5f9;
  border-radius: 0.375rem;
  color: #0f172a;
}

.builder-view__keyword-remove {
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  .material-symbols-outlined { font-size: 0.875rem; }
  &:hover { color: #dc2626; }
}

.builder-view__keyword-input-wrap {
  margin-top: 0.5rem;
}

.builder-view__keyword-input {
  width: 100%;
  box-sizing: border-box;
}

.builder-view__hero-hint {
  margin-top: 0;
}

.builder-view__hero-upload-preview {
  position: relative;
  display: inline-block;
  margin-bottom: 0.75rem;
  .builder-view__hero-upload-img {
    max-width: 20rem;
    max-height: 10rem;
    object-fit: cover;
    border-radius: 0.5rem;
    display: block;
  }
}

.builder-view__hero-upload-btn {
  margin-top: 0.25rem;
}

.builder-view__lang-toggles {
  display: flex;
  gap: 0.25rem;
}

.builder-view__lang-btn {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 9999px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #f8fafc; color: #0f172a; }
  &--active {
    background: #1a1a1a;
    color: #fff;
    border-color: #1a1a1a;
  }
}

.builder-view__url-wrap {
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
}

.builder-view__url-prefix {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #94a3b8;
  background: #f8fafc;
  white-space: nowrap;
}

.builder-view__page-input--slug {
  border: none;
  border-radius: 0;
  flex: 1;
  min-width: 6rem;
}

.builder-view__error {
  display: block;
  font-size: 0.75rem;
  color: #dc2626;
  margin-top: 0.25rem;
}

.builder-view__blocks {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.builder-view__block {
  position: relative;
  background: #fff;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:hover { border-color: #cbd5e1; }
  &--active {
    border-color: #1a1a1a;
    box-shadow: 0 0 0 2px rgba(26, 26, 26, 0.1);
  }
  &--dragging {
    opacity: 0.6;
  }
}

.builder-view__block-sidebar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 1rem;
  gap: 0.5rem;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
  z-index: 1;
}

.builder-view__block-handle {
  font-size: 1.25rem;
  color: #94a3b8;
  cursor: grab;
}

.builder-view__block-delete {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #94a3b8;
  &:hover { color: #dc2626; }
  .material-symbols-outlined { font-size: 1.125rem; }
}

.builder-view__block-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  z-index: 1;
}

.builder-view__block-content {
  padding: 2rem 2rem 2rem 3.5rem;
}

.builder-view__block-headline {
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.75rem;
  line-height: 1.3;
}

.builder-view__block-desc {
  font-size: 1rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
  &::v-deep ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    list-style-type: disc;
    list-style-position: outside;
  }
  &::v-deep ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    list-style-type: decimal;
    list-style-position: outside;
  }
  &::v-deep li {
    margin: 0.25rem 0;
    display: list-item;
  }
  &::v-deep strong { font-weight: 600; }
  &::v-deep em { font-style: italic; }
  &::v-deep a { color: inherit; text-decoration: underline; }
}

.builder-view__block-grey {
  background: #676F71;
  color: #fff;
  margin: 0 -2rem -2rem 0;
  margin-left: 1rem;
  padding: 1.5rem 1.5rem 1.75rem 1.5rem;
  min-height: 120px;
  box-sizing: border-box;
}

.builder-view__grey-preview {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 0.5rem 1rem 1rem;
  box-sizing: border-box;
}

.builder-view__grey-left {
  flex: 1;
  min-width: 0;
  max-width: 50%;
  padding-left: 24px;
  box-sizing: border-box;
  overflow-wrap: break-word;
  word-wrap: break-word;
  .builder-view__block-headline {
    color: #fff;
    font-size: 1rem;
    line-height: 1.35;
    margin-bottom: 0.5rem;
    word-wrap: break-word;
  }
}

.builder-view__grey-subtitle {
  color: #fff;
  font-size: 0.8rem;
  opacity: 0.95;
  line-height: 1.35;
}

.builder-view__grey-right {
  flex: 1;
  min-width: 0;
  max-width: 50%;
  padding-left: 1rem;
  padding-bottom: 0.5rem;
  .builder-view__block-desc {
    color: #fff;
    font-size: 0.8rem;
    line-height: 1.4;
  }
}

.builder-view__block-idea {
  margin: 0 -2rem -2rem 0;
  margin-left: 1rem;
  padding: 1rem;
  min-height: 120px;
  box-sizing: border-box;
  overflow: hidden;
  background: #fff;
}

.builder-view__idea-preview {
  display: flex;
  flex-wrap: nowrap;
  gap: 0;
  box-sizing: border-box;
  width: 100%;
}

.builder-view__idea-text {
  flex: 1 1 50%;
  min-width: 0;
  max-width: 50%;
  padding: 12px 16px 12px 24px;
  box-sizing: border-box;
  overflow: hidden;
  background: transparent;
  .builder-view__block-desc {
    font-size: 0.8rem;
    line-height: 1.4;
    color: #334155;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
}

.builder-view__idea-list {
  flex: 1 1 50%;
  min-width: 0;
  max-width: 50%;
  padding: 12px 16px;
  box-sizing: border-box;
  overflow: hidden;
  .builder-view__block-headline {
    font-size: 1rem;
    line-height: 1.3;
    margin: 0 0 0.75rem;
    color: #000;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 100%;
  }
}

.builder-view__idea-fixed-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-width: 100%;
  li {
    border-top: 1px solid #cbd5e1;
    padding: 0.4rem 0;
    font-size: 0.8rem;
    color: #64748b;
    overflow-wrap: break-word;
    word-wrap: break-word;
    .index {
      margin-right: 0.75rem;
    }
  }
}

.builder-view__section-hint {
  font-size: 0.75rem;
  color: #94a3b8;
  margin: 0 0 0.75rem;
}

.builder-view__block-cooperate {
  padding: 1rem;
  min-height: 100px;
  box-sizing: border-box;
}

.builder-view__cooperate-preview {
  background: #374151;
  color: #fff;
  padding: 1rem 1.25rem;
  border-radius: 4px;
  text-align: center;
  .builder-view__block-headline {
    color: #fff;
    font-size: 0.95rem;
    margin: 0 0 0.5rem;
  }
  .builder-view__block-desc {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.8rem;
    line-height: 1.4;
    margin: 0 0 0.75rem;
    white-space: pre-wrap;
    word-break: break-word;
  }
}

.builder-view__cooperate-btn {
  display: inline-block;
  font-size: 0.75rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid #fff;
  border-radius: 4px;
}

.builder-view__block-best-material {
  padding: 1rem 1rem 1rem 3.5rem;
  min-height: 100px;
  box-sizing: border-box;
  background: #fff;
}

.builder-view__best-material-preview {
  display: flex;
  gap: 1rem;
  .builder-view__best-material-left {
    width: 50%;
    min-width: 0;
    padding-left: 0.5rem;
    box-sizing: border-box;
    overflow-wrap: break-word;
    word-wrap: break-word;
    .builder-view__block-headline {
      font-size: 0.95rem;
      margin: 0;
      overflow-wrap: break-word;
      word-wrap: break-word;
    }
  }
  .builder-view__best-material-right {
    width: 50%;
    min-width: 0;
    .builder-view__block-desc {
      font-size: 0.8rem;
      line-height: 1.4;
      margin: 0;
    }
  }
}

.builder-view__block-gallery {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.builder-view__gallery-thumb {
  width: 8rem;
  height: 8rem;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #f1f5f9;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &--empty {
    border: 1px dashed #cbd5e1;
    .material-symbols-outlined { font-size: 2rem; color: #94a3b8; }
  }
}

.builder-view__block-partners {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  max-width: 950px;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
}

.builder-view__partners-thumb {
  background: #fff;
  box-sizing: border-box;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
    img {
      transform: scale(1.15);
    }
  }
  img {
    width: 100%;
    height: auto;
    display: block;
    vertical-align: top;
    transition: transform 0.2s ease;
  }
  &--empty {
    aspect-ratio: 1;
    min-height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    .material-symbols-outlined { font-size: 2rem; color: #94a3b8; }
    &:hover {
      border-color: #cbd5e1;
      box-shadow: none;
      transform: none;
    }
  }
}

.builder-view__section-hint {
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0 0 0.75rem;
}

.builder-view__block-type-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.builder-view__block-type-list .builder-view__block-menu-item {
  justify-content: flex-start;
}

.builder-view__block-image .builder-view__image-preview {
  max-width: 100%;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #f1f5f9;
  img {
    width: 100%;
    height: auto;
    display: block;
  }
}

.builder-view__block-image .builder-view__image-placeholder {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  border: 1px dashed #cbd5e1;
  color: #64748b;
  font-size: 0.875rem;
  .material-symbols-outlined { font-size: 2rem; color: #94a3b8; }
}

.builder-view__block-hero .builder-view__hero-preview {
  max-width: 100%;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #f1f5f9;
  img {
    width: 100%;
    height: auto;
    max-height: 200px;
    object-fit: cover;
    display: block;
  }
}

.builder-view__block-hero .builder-view__hero-placeholder {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  border: 1px dashed #cbd5e1;
  color: #64748b;
  font-size: 0.875rem;
  .material-symbols-outlined { font-size: 2rem; color: #94a3b8; }
}

.builder-view__block-caption {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0.75rem 0 0;
  line-height: 1.4;
}

.builder-view__image-edit-preview {
  position: relative;
  display: inline-block;
  margin-bottom: 0.75rem;
  img {
    max-width: 12rem;
    max-height: 8rem;
    object-fit: cover;
    border-radius: 0.5rem;
    display: block;
  }
}

.builder-view__image-remove {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
}

.builder-view__video-embed-wrap {
  width: 100%;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #000;
  aspect-ratio: 16 / 9;
}

.builder-view__video-iframe {
  width: 100%;
  height: 100%;
  display: block;
}

.builder-view__block-video .builder-view__video-placeholder {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  border: 1px dashed #cbd5e1;
  color: #64748b;
  font-size: 0.875rem;
  .material-symbols-outlined { font-size: 2rem; color: #94a3b8; }
}

.builder-view__block-video-gallery {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.builder-view__block-tiles {
  padding: 1.5rem 2rem 1.5rem 3.5rem;
}

.builder-view__tiles-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.builder-view__tile-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  &--empty {
    border-style: dashed;
    min-height: 8rem;
    justify-content: center;
    color: #94a3b8;
    .material-symbols-outlined { font-size: 2rem; }
  }
}

.builder-view__tile-preview-img {
  width: 100%;
  aspect-ratio: 4 / 3;
  background: #f1f5f9;
  border-radius: 0.5rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .material-symbols-outlined {
    font-size: 2rem;
    color: #94a3b8;
  }
}

.builder-view__tile-preview-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  text-align: center;
  line-height: 1.3;
}

.builder-view__tile-preview-link {
  font-size: 0.8125rem;
  color: #64748b;
}

.builder-view__tiles-edit {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.builder-view__tiles-edit-item {
  padding: 1rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  position: relative;
}

.builder-view__tile-edit-preview {
  position: relative;
  display: inline-block;
  margin-bottom: 0.75rem;
  img {
    max-width: 12rem;
    max-height: 8rem;
    object-fit: cover;
    border-radius: 0.5rem;
    display: block;
  }
}

.builder-view__tile-image-remove {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
}

.builder-view__tile-add-img {
  margin-bottom: 0.75rem;
}

.builder-view__tile-remove {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
}

.builder-view__video-gallery-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  font-size: 0.875rem;
  color: #64748b;
  min-width: 8rem;
  .material-symbols-outlined { font-size: 1.5rem; color: #94a3b8; }
  &--empty {
    border-style: dashed;
    color: #94a3b8;
    .material-symbols-outlined { font-size: 1.25rem; }
  }
}

.builder-view__video-gallery-edit {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.builder-view__video-gallery-edit-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  .builder-view__input { flex: 1; }
}

.builder-view__append {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
  padding: 2rem;
  border: 2px dashed #cbd5e1;
  border-radius: 1rem;
  background: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.15s;
  .material-symbols-outlined { font-size: 1.5rem; }
  &:hover {
    border-color: #94a3b8;
    color: #64748b;
    background: #f8fafc;
  }
}

.builder-view__sidebar {
  flex: 1 1 0;
  min-width: 400px;
  background: #fff;
  border-left: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.builder-view__sidebar-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.builder-view__sidebar-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin: 0;
  text-transform: uppercase;
}

.builder-view__sidebar-subtitle {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0.25rem 0 0;
}

.builder-view__sidebar-close {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #94a3b8;
  &:hover { color: #0f172a; }
  .material-symbols-outlined { font-size: 1.25rem; }
}

.builder-view__sidebar-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  padding-right: 1.75rem;
  box-sizing: border-box;
}

.builder-view__section {
  margin-bottom: 1.5rem;
}

.builder-view__section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.builder-view__section-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin: 0 0 0.75rem;
  text-transform: uppercase;
}

.builder-view__section-chevron {
  font-size: 1.25rem;
  color: #94a3b8;
}

.builder-view__section--collapsed .builder-view__section-title {
  margin: 0;
}

.builder-view__field {
  margin-bottom: 1rem;
}

.builder-view__label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.375rem;
}

.builder-view__input {
  width: 100%;
  padding: 0.5rem 1rem 0.5rem 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #94a3b8; }
  &--sm { padding: 0.375rem 0.5rem; font-size: 0.8125rem; }
}

.builder-view__textarea {
  width: 100%;
  padding: 0.5rem 1rem 0.5rem 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  outline: none;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  &:focus { border-color: #94a3b8; }
}

.builder-view__wysiwyg {
  width: 100%;
  position: relative;
  z-index: 2;
}

.builder-view__wysiwyg :deep(.tox-tinymce) {
  z-index: 1;
}

.builder-view__wysiwyg :deep(.tox-edit-area__iframe) {
  background: #fff;
}

.builder-view__wysiwyg :deep(.tox-tinymce .tox-edit-area) {
  background: #fff;
}

.builder-view__toggle-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
  color: #64748b;
  cursor: pointer;
}

.builder-view__toggle {
  width: 2.5rem;
  height: 1.25rem;
  border-radius: 9999px;
  background: #e2e8f0;
  border: none;
  padding: 0.125rem;
  cursor: pointer;
  transition: background 0.15s;
  &--on { background: #1a1a1a; }
}

.builder-view__toggle-knob {
  display: block;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
  transition: transform 0.15s;
}

.builder-view__toggle--on .builder-view__toggle-knob {
  transform: translateX(1.25rem);
}

.builder-view__align-btns {
  display: flex;
  gap: 0.25rem;
}

.builder-view__align-btn {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 0.5rem;
  cursor: pointer;
  color: #64748b;
  transition: all 0.15s;
  .material-symbols-outlined { font-size: 1.25rem; }
  &:hover { color: #0f172a; background: #f8fafc; }
  &--active {
    background: #1a1a1a;
    color: #fff;
    border-color: #1a1a1a;
  }
}

.builder-view__gallery-edit {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.builder-view__gallery-edit-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  position: relative;
}

.builder-view__gallery-edit-thumb {
  width: 3rem;
  height: 3rem;
  object-fit: cover;
  border-radius: 0.375rem;
  flex-shrink: 0;
}

.builder-view__gallery-edit-item .builder-view__input--sm {
  flex: 1;
}

.builder-view__gallery-remove {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #94a3b8;
  &:hover { color: #dc2626; }
  .material-symbols-outlined { font-size: 1.125rem; }
}

.builder-view__file-hidden {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.builder-view__save-props {
  margin: 0 1.5rem 1.5rem;
  padding: 0.75rem 1.5rem;
  background: #1a1a1a;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: background 0.15s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover:not(:disabled) { background: #334155; }
  &:disabled { opacity: 0.6; cursor: default; }
}

.builder-view__block-menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #0f172a;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
  .material-symbols-outlined { font-size: 1.25rem; color: #64748b; }
  &:hover { background: #f8fafc; }
}

.builder-view__block-faq {
  padding-bottom: 1rem;
}

.builder-view__faq-preview-list {
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  border-top: 1px solid #e2e8f0;
}

.builder-view__faq-preview-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-top: 1px solid #e2e8f0;
  font-size: 0.875rem;
  color: #64748b;
}

.builder-view__faq-preview-num {
  flex-shrink: 0;
  width: 1.5rem;
  font-weight: 600;
  color: #0f172a;
}

.builder-view__idea-empty {
  margin: 0.75rem 0 0;
  font-size: 0.8125rem;
  color: #94a3b8;
}

.builder-view__faq-preview-empty {
  margin: 0.75rem 0 0;
  font-size: 0.8125rem;
  color: #94a3b8;
}

.builder-view__faq-edit {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.builder-view__faq-edit-item {
  padding: 1rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  position: relative;
}

.builder-view__faq-remove {
  margin-top: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8125rem;
}
</style>

<style lang="scss">
/* Unscoped so v-html list/formatting renders; .builder-view__block-desc is component-specific */
.builder-view__block-desc {
  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    list-style-type: disc !important;
    list-style-position: outside;
  }
  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    list-style-type: decimal !important;
    list-style-position: outside;
  }
  li {
    margin: 0.25rem 0;
    display: list-item;
  }
  strong { font-weight: 600; }
  em { font-style: italic; }
  a { color: inherit; text-decoration: underline; }
}
</style>
